/**
 * Shared optional co-borrower step tail (WI-TV-W7-002).
 *
 * WI-TV-W7-001 delivered the co-borrower *answer model* — namespaced value keys,
 * one sanitizer, one clear — but no visible step. This module is that visible
 * cadence, and both journeys append exactly this one list after the primary
 * email step, so purchase and refinance cannot drift in wording, validation,
 * clearing, accessibility, or payload projection.
 *
 * The cadence is fully optional and opt-in:
 *
 * 1. **Add a co-borrower?** — Yes/No. The borrower must answer the explicit
 *    decision; No clears every secondary answer immediately and is the only
 *    primary-only path through this step.
 * 2. **Co-borrower name** — first and last, required, but only after Yes.
 * 3. **Co-borrower phone** — required, using the existing `(000) 000-0000`
 *    display and canonical ten-digit storage primitive.
 * 4. **Co-borrower email** — required, using the existing structural rule.
 * 5. **Co-borrower mailing address** — optional street/city/state/ZIP. An
 *    untouched address never blocks Continue; a supplied state or ZIP must
 *    satisfy the same authoritative validators the property address uses.
 *
 * Every opted-in data step carries a visible, keyboard- and screen-reader
 * accessible **Remove co-borrower** action. It clears every secondary value and
 * asks the shell to reconcile, which returns the borrower to the decision step
 * with nothing validated and nothing submitted. Because the four data steps
 * declare `applies` and `valueKeys`, a declined or removed co-borrower has no
 * steps in the cadence and holds no values: progress renumbers
 * immediately and no hidden secondary answer can survive into a payload.
 *
 * The co-borrower identity/contact/address controls use native autocomplete
 * *sectioning* (`section-co-borrower …`), so browsers keep autofilling both
 * people without ever offering the applicant's own details for the co-borrower
 * (or the reverse). No secondary credit, military, employment, or income answer
 * exists here: the military question stays application-level.
 */
import { STATE_OPTIONS, type ReferralFormState, type SelectOption } from "../models/referral";
import { isValidStateCode } from "../form/validation";
import { isValidEmailInput, isValidPhoneInput, phoneDigits } from "./borrower-steps";
import {
  COBORROWER_CITY_KEY,
  COBORROWER_EMAIL_KEY,
  COBORROWER_FIRST_NAME_KEY,
  COBORROWER_LAST_NAME_KEY,
  COBORROWER_OPT_IN_KEY,
  COBORROWER_OPT_IN_VALUE,
  COBORROWER_PHONE_KEY,
  COBORROWER_STATE_KEY,
  COBORROWER_STREET_KEY,
  COBORROWER_VALUE_KEYS,
  COBORROWER_ZIP_KEY,
  isCoBorrowerOptedIn,
  coBorrowerClearPatch,
} from "./coborrower-values";
import {
  PHONE_DISPLAY_MASK,
  bindPhoneInput,
  bindRadioGroup,
  bindValueInputs,
  dispatchStepReset,
  formatPhoneDisplay,
  radioGroupHtml,
  selectFieldHtml,
  textFieldHtml,
} from "./step-fields";
import type { WizardFieldError, WizardStep, WizardValues } from "./wizard-machine";

/** The decision answer that opts the application in (the model's own value). */
export const COBORROWER_DECISION_YES = COBORROWER_OPT_IN_VALUE;

/**
 * The decision answer that declines. It is deliberately *not* the affirmative
 * value, so `isCoBorrowerOptedIn` is false for it and a declined application is
 * exactly as co-borrower-free as an unanswered or skipped one.
 */
export const COBORROWER_DECISION_NO = "no";

/** The two answers to the decision question. */
export const COBORROWER_DECISION_OPTIONS: readonly SelectOption[] = [
  { value: COBORROWER_DECISION_YES, label: "Yes, add a co-borrower" },
  { value: COBORROWER_DECISION_NO, label: "No, it's just me" },
];

/**
 * Native autocomplete section prefix for every co-borrower identity, contact,
 * and address control. The primary borrower keeps the unsectioned tokens it
 * already uses (`given-name`, `family-name`, `tel`, `email`), so the two people
 * are distinguishable to the browser without changing the applicant's contract.
 */
export const COBORROWER_AUTOCOMPLETE_SECTION = "section-co-borrower";

/** A mailing ZIP is exactly five digits, like every other ZIP the SPA accepts. */
export const COBORROWER_ZIP_REGEX = /^\d{5}$/;

/** The mailing address keys, in DOM order. */
export const COBORROWER_MAILING_KEYS = [
  COBORROWER_STREET_KEY,
  COBORROWER_CITY_KEY,
  COBORROWER_STATE_KEY,
  COBORROWER_ZIP_KEY,
] as const;

/** Autocomplete token for a co-borrower control, in its own section. */
function section(token: string): string {
  return `${COBORROWER_AUTOCOMPLETE_SECTION} ${token}`;
}

/** True when the answers currently opt the application into a co-borrower. */
function optedIn(values: WizardValues): boolean {
  return isCoBorrowerOptedIn(values.snapshot());
}

/**
 * Clears every co-borrower value, opt-in included. This is the single clear the
 * decision step's Skip and the Remove action both use, so no combination of
 * decline, skip, remove, and re-add can resurrect a stale secondary answer.
 */
function clearCoBorrower(values: WizardValues): void {
  for (const [key, value] of Object.entries(coBorrowerClearPatch())) values.set(key, value);
}

/**
 * The accessible Remove affordance rendered by every opted-in data step, with a
 * standing note explaining what it does before it is activated.
 */
function removeActionHtml(): string {
  return `
    <div class="wizard-step-remove">
      <button
        type="button"
        class="wizard-btn wizard-btn-link"
        data-coborrower-remove
        aria-describedby="coborrower-remove-note"
      >
        Remove co-borrower
      </button>
      <p class="wizard-hint" id="coborrower-remove-note">
        Removing the co-borrower clears their information and takes you back to the co-borrower
        question. Nothing is submitted.
      </p>
    </div>`;
}

/**
 * Binds the Remove action: the step hands the shell the clear it owns, and the
 * shell decides whether the removal happens — it refuses one while a submission
 * is in flight or after an accepted referral, in which case nothing is cleared.
 * On an accepted removal the shell prunes, relocates once (the displayed step no
 * longer exists), and re-renders, validating and submitting nothing.
 */
function bindRemoveAction(container: HTMLElement, values: WizardValues): void {
  const button = container.querySelector<HTMLButtonElement>("[data-coborrower-remove]");
  if (!button) return;
  button.addEventListener("click", () => {
    dispatchStepReset(button, () => clearCoBorrower(values));
  });
}

/**
 * Step — Add a co-borrower?
 *
 * Answering No clears every secondary answer the moment the choice is made
 * rather than holding them until Continue, so an answer the borrower can no
 * longer see is never carried, snapshot, validated, or submitted. The decision
 * itself is retained (Back shows the choice again); only the affirmative value
 * opts in.
 */
export const coBorrowerDecisionStep: WizardStep = {
  id: "coborrower-decision",
  title: "Co-borrower",
  validate: (values: WizardValues): WizardFieldError[] => {
    const selected = values.get(COBORROWER_OPT_IN_KEY);
    const valid = COBORROWER_DECISION_OPTIONS.some((option) => option.value === selected);
    if (valid) return [];
    return [
      {
        field: `${COBORROWER_OPT_IN_KEY}-${COBORROWER_DECISION_NO}`,
        message: "Select whether you want to add a co-borrower to continue.",
      },
    ];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc" id="coborrower-decision-desc">
        Would you like to add a co-borrower to this application? Select No if it is just you.
      </p>
      ${radioGroupHtml(
        COBORROWER_OPT_IN_KEY,
        "Add a co-borrower?",
        COBORROWER_DECISION_OPTIONS,
        values.get(COBORROWER_OPT_IN_KEY),
        { autofocus: true, labelledBy: "coborrower-decision-desc" },
      )}`;

    bindRadioGroup(container, COBORROWER_OPT_IN_KEY, values, (answer) => {
      // Declining clears every co-borrower answer immediately; the decision
      // itself stays so the borrower sees the choice they made.
      if (answer === COBORROWER_DECISION_YES) return;
      for (const key of COBORROWER_VALUE_KEYS) {
        if (key !== COBORROWER_OPT_IN_KEY) values.set(key, "");
      }
    });
  },
};

/** Co-borrower name. Both parts required, and only once the borrower opted in. */
export const coBorrowerNameStep: WizardStep = {
  id: "coborrower-name",
  title: "Co-borrower name",
  valueKeys: [COBORROWER_FIRST_NAME_KEY, COBORROWER_LAST_NAME_KEY],
  applies: optedIn,
  validate: (values: WizardValues): WizardFieldError[] => {
    const errors: WizardFieldError[] = [];
    if (values.get(COBORROWER_FIRST_NAME_KEY).trim() === "") {
      errors.push({
        field: COBORROWER_FIRST_NAME_KEY,
        message: "Enter your co-borrower's first name to continue.",
      });
    }
    if (values.get(COBORROWER_LAST_NAME_KEY).trim() === "") {
      errors.push({
        field: COBORROWER_LAST_NAME_KEY,
        message: "Enter your co-borrower's last name to continue.",
      });
    }
    return errors;
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">Your co-borrower's legal name.</p>
      ${textFieldHtml(
        COBORROWER_FIRST_NAME_KEY,
        "Co-borrower first name",
        values.get(COBORROWER_FIRST_NAME_KEY),
        { required: true, autocomplete: section("given-name") },
      )}
      ${textFieldHtml(
        COBORROWER_LAST_NAME_KEY,
        "Co-borrower last name",
        values.get(COBORROWER_LAST_NAME_KEY),
        { required: true, autocomplete: section("family-name") },
      )}
      ${removeActionHtml()}`;

    bindValueInputs(container, values);
    bindRemoveAction(container, values);
  },
};

/**
 * Co-borrower phone. Required, and bound to the same phone primitive the
 * primary contact uses, so display formatting, canonical ten-digit storage,
 * paste, autofill-by-`change`, IME composition, and selection handling are
 * identical rather than reimplemented.
 */
export const coBorrowerPhoneStep: WizardStep = {
  id: "coborrower-phone",
  title: "Co-borrower phone number",
  valueKeys: [COBORROWER_PHONE_KEY],
  applies: optedIn,
  validate: (values: WizardValues): WizardFieldError[] => {
    const raw = values.get(COBORROWER_PHONE_KEY);
    if (raw.trim() === "") {
      return [
        {
          field: COBORROWER_PHONE_KEY,
          message: "Enter your co-borrower's phone number to continue.",
        },
      ];
    }
    if (!isValidPhoneInput(raw)) {
      return [
        {
          field: COBORROWER_PHONE_KEY,
          message: "Enter a 10-digit US phone number, for example (512) 555-0134.",
        },
      ];
    }
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">A phone number where we can reach your co-borrower.</p>
      ${textFieldHtml(
        COBORROWER_PHONE_KEY,
        "Co-borrower phone number",
        formatPhoneDisplay(values.get(COBORROWER_PHONE_KEY)),
        {
          required: true,
          type: "tel",
          inputmode: "tel",
          autocomplete: section("tel"),
          placeholder: PHONE_DISPLAY_MASK,
          describedBy: "coborrower-phone-hint",
        },
      )}
      <p class="wizard-hint" id="coborrower-phone-hint">Format: ${PHONE_DISPLAY_MASK}</p>
      ${removeActionHtml()}`;

    bindPhoneInput(container, COBORROWER_PHONE_KEY, values);
    bindRemoveAction(container, values);
  },
};

/** Co-borrower email. Required, using the existing structural email rule. */
export const coBorrowerEmailStep: WizardStep = {
  id: "coborrower-email",
  title: "Co-borrower email",
  valueKeys: [COBORROWER_EMAIL_KEY],
  applies: optedIn,
  validate: (values: WizardValues): WizardFieldError[] => {
    const raw = values.get(COBORROWER_EMAIL_KEY);
    if (raw.trim() === "") {
      return [
        {
          field: COBORROWER_EMAIL_KEY,
          message: "Enter your co-borrower's email address to continue.",
        },
      ];
    }
    if (!isValidEmailInput(raw)) {
      return [
        {
          field: COBORROWER_EMAIL_KEY,
          message: "Enter a valid email address, for example name@example.com.",
        },
      ];
    }
    return [];
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc">We'll use this to follow up with your co-borrower.</p>
      ${textFieldHtml(
        COBORROWER_EMAIL_KEY,
        "Co-borrower email address",
        values.get(COBORROWER_EMAIL_KEY),
        {
          required: true,
          type: "email",
          inputmode: "email",
          autocomplete: section("email"),
        },
      )}
      ${removeActionHtml()}`;

    bindValueInputs(container, values);
    bindRemoveAction(container, values);
  },
};

/**
 * Co-borrower mailing address. Every field is optional: an untouched address is
 * continuable and skippable, and nothing is invented when it is left blank. A
 * supplied state or ZIP is still held to the authoritative rules the property
 * address uses, so a malformed optional answer blocks Continue with reachable,
 * associated guidance rather than reaching the payload.
 */
export const coBorrowerMailingStep: WizardStep = {
  id: "coborrower-mailing",
  title: "Co-borrower mailing address",
  optional: true,
  valueKeys: [...COBORROWER_MAILING_KEYS],
  applies: optedIn,
  validate: (values: WizardValues): WizardFieldError[] => {
    const errors: WizardFieldError[] = [];
    // Errors are produced in DOM order, so the first one focuses the first
    // invalid control on the step.
    const state = values.get(COBORROWER_STATE_KEY).trim();
    if (state !== "" && !isValidStateCode(state)) {
      errors.push({
        field: COBORROWER_STATE_KEY,
        message: "Select a valid US state or territory, or leave it blank.",
      });
    }
    const zip = values.get(COBORROWER_ZIP_KEY).trim();
    if (zip !== "" && !COBORROWER_ZIP_REGEX.test(zip)) {
      errors.push({
        field: COBORROWER_ZIP_KEY,
        message: "Enter a 5-digit ZIP code, or leave it blank.",
      });
    }
    return errors;
  },
  // An explicit Skip abandons the address the borrower chose to leave behind,
  // and only that: the co-borrower themselves stays on the application.
  onSkip: (values: WizardValues): void => {
    for (const key of COBORROWER_MAILING_KEYS) values.set(key, "");
  },
  render: (container: HTMLElement, values: WizardValues): void => {
    container.innerHTML = `
      <p class="wizard-step-desc" id="coborrower-mailing-desc">
        Where does your co-borrower receive mail? This is optional — you can leave it blank or skip
        it.
      </p>
      <fieldset class="wizard-field-group" data-coborrower-address-group>
        <legend class="wizard-field-group-legend">Co-borrower mailing address</legend>
        ${textFieldHtml(
          COBORROWER_STREET_KEY,
          "Co-borrower street address",
          values.get(COBORROWER_STREET_KEY),
          { autocomplete: section("address-line1") },
        )}
        ${textFieldHtml(COBORROWER_CITY_KEY, "Co-borrower city", values.get(COBORROWER_CITY_KEY), {
          autocomplete: section("address-level2"),
        })}
        ${selectFieldHtml(
          COBORROWER_STATE_KEY,
          "Co-borrower state",
          "Select a state",
          STATE_OPTIONS,
          values.get(COBORROWER_STATE_KEY),
          { required: false, autocomplete: section("address-level1") },
        )}
        ${textFieldHtml(
          COBORROWER_ZIP_KEY,
          "Co-borrower ZIP code",
          values.get(COBORROWER_ZIP_KEY),
          {
            inputmode: "numeric",
            maxlength: 5,
            autocomplete: section("postal-code"),
          },
        )}
      </fieldset>
      ${removeActionHtml()}`;

    bindValueInputs(container, values);
    bindRemoveAction(container, values);
  },
};

/**
 * Projects the co-borrower answers of a value snapshot onto a referral form
 * state, in place. Both journey projections call exactly this, so purchase and
 * refinance produce the same `coBorrower` payload for the same answers.
 *
 * The opt-in is the whole gate: an application that declined, skipped, or
 * removed its co-borrower sets `includeCoBorrower` false and leaves the empty
 * secondary state alone, which is what makes `buildPayload` omit the property
 * entirely rather than emit a blank object. There is deliberately no secondary
 * credit answer to project: `coBorrower.creditScoreRange` stays empty because no
 * co-borrower credit question exists.
 */
export function applyCoBorrowerFormState(
  state: ReferralFormState,
  values: Readonly<Record<string, string>>,
): void {
  if (!isCoBorrowerOptedIn(values)) return;
  const read = (key: string): string => values[key] ?? "";
  state.includeCoBorrower = true;
  state.coBorrower.firstName = read(COBORROWER_FIRST_NAME_KEY).trim();
  state.coBorrower.lastName = read(COBORROWER_LAST_NAME_KEY).trim();
  state.coBorrower.email = read(COBORROWER_EMAIL_KEY).trim();
  // The payload carries exactly the ten digits the borrower intended, whatever
  // formatting characters they typed around them.
  state.coBorrower.phone = phoneDigits(read(COBORROWER_PHONE_KEY));
  state.coBorrower.streetAddress = read(COBORROWER_STREET_KEY).trim();
  state.coBorrower.city = read(COBORROWER_CITY_KEY).trim();
  state.coBorrower.state = read(COBORROWER_STATE_KEY).trim();
  state.coBorrower.zipCode = read(COBORROWER_ZIP_KEY).trim();
}

/**
 * The shared optional co-borrower tail, in cadence order. Both journeys append
 * exactly this list after their primary email step.
 */
export function createCoBorrowerSteps(): WizardStep[] {
  return [
    coBorrowerDecisionStep,
    coBorrowerNameStep,
    coBorrowerPhoneStep,
    coBorrowerEmailStep,
    coBorrowerMailingStep,
  ];
}
